import Parse from "parse";
import { useMemo } from "react";
import { getUsername } from "../../../../_shared";
import { useSubordinateRoles } from "../../../../_shared/role/hooks/useSubordinateRoles";
import { _User } from "../../../../js/parsegenerated";
import { useTicketAssignment } from "./useTicketAssignment";

enum TooltipColor {
  notAssigned = "#FFA500", //orange
  assignedRole = "#1E90FF", // blue
  assignedUser = "#32CD32", // green
  hasNoAssignment = "#000", // black
}

type TooltipProps = {
  text: string;
  color: string;
  translation: {};
};

export const useAssignedIconProps = () => {
  const { assignedRoles, assignedUsers } = useTicketAssignment();
  const roles = useSubordinateRoles(Parse.User.current());
  const tooltipProps = useMemo(
    () => getTooltip(),
    [assignedRoles, assignedUsers, roles]
  );

  const iconStyle = {
    border: `1px solid ${tooltipProps.color}`,
    borderRadius: "50%",
    padding: 2,
    cursor: "pointer",
    color: tooltipProps.color,
  };

  /**
   * Depending on the assigned roles and users, return the appropriate tooltip text and color.
   * @param assignedRoles the roles assigned to the ticket
   * @param assignedUsers the users assigned to the ticket
   * @returns the tooltip text, translation attributes and color
   */
  function getTooltip() {
    const tooltipProps: TooltipProps = {
      text: "",
      color: undefined,
      translation: {},
    };
    const { hasRoleAssigned, hasUserAssigned } = isUserAssigned();

    if (assignedRoles.length <= 0 && assignedUsers.length <= 0) {
      tooltipProps.text = "maintenance:ticket.tilemonitor.assignedusers.none";
      tooltipProps.color = TooltipColor.hasNoAssignment;
    } else if (assignedRoles.length > 0 && assignedUsers.length <= 0) {
      tooltipProps.text = "maintenance:ticket.tilemonitor.assignedusers.roles";
      tooltipProps.translation = {
        roles: assignedRoles.map((role) => role.get("label")).join(", "),
      };
      tooltipProps.color = hasRoleAssigned
        ? TooltipColor.assignedRole
        : TooltipColor.notAssigned;
    } else if (assignedRoles.length <= 0 && assignedUsers.length > 0) {
      tooltipProps.text = "maintenance:ticket.tilemonitor.assignedusers.users";
      tooltipProps.translation = {
        users: assignedUsers
          .map((user) => getUsername(user as _User))
          .join(", "),
      };
      tooltipProps.color = hasUserAssigned
        ? TooltipColor.assignedUser
        : TooltipColor.notAssigned;
    } else {
      tooltipProps.text = "maintenance:ticket.tilemonitor.assignedusers.both";
      tooltipProps.translation = {
        users: assignedUsers
          .map((user) => getUsername(user as _User))
          .join(", "),
        roles: assignedRoles.map((role) => role.get("label")).join(", "),
      };
      tooltipProps.color = hasUserAssigned
        ? TooltipColor.assignedUser
        : hasRoleAssigned
          ? TooltipColor.assignedRole
          : TooltipColor.notAssigned;
    }

    return tooltipProps;
  }

  function isUserAssigned() {
    const user = Parse.User.current();
    if (!user) return { hasRoleAssigned: false, hasUserAssigned: false };

    const hasUserAssigned = assignedUsers.some((u) => u.id === user.id);
    const hasRoleAssigned = assignedRoles.some((role) =>
      roles.find((r) => r.id === role.id)
    );

    return { hasUserAssigned, hasRoleAssigned };
  }

  return { iconStyle, tooltipProps };
};
